import { LineItemAddDto } from 'src/items-list/business-logic/service/interfaces/line-item-add.dto';
import { IItemLookupCriteria } from 'src/items-list/business-logic/service/product-lookup';
import {
  getLinkedGroupItems,
  ItemLookupByPolicyResponse,
} from 'src/items-list/business-logic/service/product-lookup-by-policy';
import { EntryMethod } from '../common/enumerations/entry-method';
import { ItemCategories } from '../common/enumerations/rti-enums/item-categories';
import { getDefaultWeightUnitOfMeasure } from '../common/helpers/localized-description-helper';
import { IItemLookup, ILinkedItemsGroup } from '../common/interfaces/dtos/IItemLookup';
import { IScanData } from '../common/interfaces/dtos/IScanData';
import { ILineItemAddData, lineItemAdd } from '../items-list/business-logic/line-item-add';
import { handleTareWeightItems } from '../tare-weight-items/business-logic/handle-tare-weight-items';
import { handleWarrantyItems } from '../warranty-item/business-logic/handle-warranty-items';
import { NoneItemId, sellLinkedItem } from './sell-linked-item';
import { addTareWeightGroup } from './tare-weight-groups-operation';

export const handleLinkedItemGroup = async (itemAddData: ILineItemAddData): Promise<void> => {
  const { itemLookup, lineItem } = itemAddData;
  // This fixes the bug for RTI
  if (
    itemLookup.linkedItemsGroup?.groupCategory !== ItemCategories.Tare &&
    itemLookup.linkedItemsGroup?.groupCategory !== ItemCategories.Warranty
  ) {
    return lineItemAdd(itemAddData);
  }

  if (
    itemLookup.linkedItemsGroup?.linkedItems.length === 1 &&
    itemLookup.linkedItemsGroup.minLinkPortions
  ) {
    return handleSingleLinkedItem(lineItem, itemLookup);
  }

  const emptyILinkedItems: ILinkedItemsGroup = {
    linkedItems: [],
  };
  const linkedItemDetails = await getLinkedItemsDetails(
    itemLookup.linkedItemsGroup ?? emptyILinkedItems,
  );

  const linkedItems = linkedItemDetails.itemLookupData;
  const groupCategory =
    linkedItemDetails.groupCategory ?? itemLookup.linkedItemsGroup?.groupCategory;

  // Deposit item always get added directly
  if (groupCategory === ItemCategories.Deposit) {
    return lineItemAdd(itemAddData);
  }

  if (linkedItems && linkedItems.length <= 1) {
    return handleSingleLinkedItem(lineItem, itemLookup);
  }

  switch (groupCategory) {
    case ItemCategories.Tare:
      addTareWeightGroup(lineItem.item.id, linkedItems, itemLookup.isManualTarePercentageEnabled);
      return handleTareWeightItems(lineItem, itemLookup, linkedItems);
    case ItemCategories.Warranty:
      return handleWarrantyItems(lineItem, itemLookup, linkedItems);
    // default:
    //   handleGroupItem()
  }
};

export const handleSingleLinkedItem = async (
  lineItem: LineItemAddDto,
  itemLookup: IItemLookup,
): Promise<void> => {
  const linkedItemLookup = itemLookup.linkedItemsGroup?.linkedItems[0];
  if (linkedItemLookup) {
    await sellLinkedItem(lineItem, itemLookup, linkedItemLookup);
  }
};

export const getLinkedItemsDetails = async (
  linkedItemsGroup: ILinkedItemsGroup,
): Promise<ItemLookupByPolicyResponse> => {
  const linkedItemIds: IScanData[] = linkedItemsGroup.linkedItems.map(
    (linkedItem: IItemLookup): IScanData => ({
      data: linkedItem.itemId,
    }),
  );
  const itemLookupCriteria: IItemLookupCriteria = {
    scanData: linkedItemIds,
    entryMethod: EntryMethod.Keyed,
    actionDescription: 'Sell',
  };
  const linkedGroupCode = linkedItemsGroup.linkedGroupCode || 'NoGroupCode';

  const linkedGroupDetails = await getLinkedGroupItems(itemLookupCriteria, linkedGroupCode);

  if (!linkedItemsGroup.requiredForSale) {
    const noSaleItemLookup: IItemLookup = {
      itemId: NoneItemId,
      description: NoneItemId,
      itemMeasurements: { weight: 0.0, unitsOfMeasure: getDefaultWeightUnitOfMeasure() },
    };
    linkedGroupDetails.itemLookupData.unshift(noSaleItemLookup);
  }

  return linkedGroupDetails;
};
